import React from 'react';
import { chunk } from 'lodash-es';
import Box from '@mui/material/Box';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import { styled } from 'sensortower-components/src/utils/styled';
import ErrorBoundary from 'sensortower-components/src/base-components/ErrorBoundary';
import { CardProps } from 'sensortower-components/src/base-components/Card';
import ContentModule from 'sensortower-components/src/base-components/ContentModule';
import { CollectionProps } from 'sensortower-components/src/base-components/Collection';
import { sidekick } from '../../utils/sidekick';

export interface CollectionImageGridProps extends Omit<CollectionProps, 'items'> {
  /**
   * Nullable because that is what the resolver sends for a collection with no items — `chunk`
   * turns null into an empty grid. Declaring it required said otherwise, and the spec covering
   * the null case only compiled because `mount()` props are untyped.
   */
  items?: CardProps[] | null;
}

const getCols = (total: number, variant: string | undefined): number => {
  if (variant === '2x2-image-grid') return 1;
  if (total <= 1) return 2;
  return 1;
};

const getRows = (index: number, total: number, variant: string | undefined): number => {
  if (variant === '2x2-image-grid') return 1;
  if (total <= 2) return 1;
  if (index === 0) return 2;
  return 1;
};

export const CollectionImageGrid = ({ id, items, sidekickLookup, variant }: CollectionImageGridProps) => {
  const itemData = chunk(items, 3) as CardProps[][];
  return (
    <ErrorBoundary>
      <Root {...sidekick(sidekickLookup)} data-testid="CollectionImageGrid">
        <ImageList
          variant={variant === '2x2-image-grid' ? 'standard' : 'quilted'}
          cols={2}
          gap={16}
          data-testid="CollectionImageGrid-imageList"
        >
          {itemData.map((item, i) => (
            <React.Fragment key={`collection-grid-${id}-${i}`}>
              {item?.map((card: CardProps, index: number) => (
                <ImageListItem
                  key={card.id}
                  cols={getCols(item.length, variant)}
                  rows={getRows(index, item.length, variant)}
                  data-testid="CollectionImageGrid-imageListItem"
                >
                  <ContentModule {...card} variant="media-grid" />
                </ImageListItem>
              ))}
            </React.Fragment>
          ))}
        </ImageList>
      </Root>
    </ErrorBoundary>
  );
};

const Root = styled(Box, {
  name: 'CollectionImageGrid',
  slot: 'Root',
})<{ variant?: string }>(({ theme }) => ({
  [theme.breakpoints.down('lg')]: {
    '& [class*="MuiPaper-root"]': {
      padding: '0',
    },
  },
  '& [class*="MuiImageList-root"]': {
    margin: '0 auto',
  },
}));

export default CollectionImageGrid;
